import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { PackageSearch } from "lucide-react";
import "./header.css";
import { useCart } from "@/context/CartContext";
import { categories as mockCategories } from "@/mocks/products";
import { fetchNuvemshopCategories, isNuvemshopEnabled, type NuvemshopCategory } from "@/services/nuvemshop";

const navItems = [
  { label: "Início", href: "/" },
  { label: "Categorias", href: "/produtos", children: true },
  { label: "Lançamentos", href: "/produtos?colecao=novidades" },
  { label: "Promoções", href: "/produtos?colecao=promocoes" },
  { label: "Mais Vendidos", href: "/produtos?colecao=vendidos" },
];

const categoryHref = (id: string) => `/produtos?categoria=${encodeURIComponent(id)}`;

// Categorias reais da loja (Nuvemshop). A lista vem do cache do servidor e é
// buscada uma vez por visita; se falhar, o menu mostra só "Todos os produtos".
function useStoreCategories(): NuvemshopCategory[] {
  const [list, setList] = useState<NuvemshopCategory[]>(
    isNuvemshopEnabled() ? [] : mockCategories.map((c) => ({ id: c.id, label: c.label }))
  );
  useEffect(() => {
    if (!isNuvemshopEnabled()) return;
    let active = true;
    fetchNuvemshopCategories()
      .then((categories) => { if (active) setList(categories); })
      .catch(() => { /* sem categorias: só "Todos os produtos" */ });
    return () => { active = false; };
  }, []);
  return list;
}

function Logo() {
  return (
    <Link to="/" aria-label="Rica Tanto — início" className="brand-logo flex flex-col items-center gap-0.5 group">
      <img src={`${import.meta.env.BASE_URL}rica-tanto-logo.webp`} alt="Rica Tanto" />
    </Link>
  );
}

export default function Header({ variant = "solid" }: { variant?: "overlay" | "solid" }) {
  const location = useLocation();
  const overlay = variant === "overlay";
  // Na página inicial o cabeçalho fica sobre a foto, transparente; ao rolar vira uma barra escura.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (!overlay) return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [overlay]);
  const { totalItems, openCart } = useCart();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const storeCategories = useStoreCategories();

  return (
    <header className={overlay ? `fixed inset-x-0 top-0 z-40 header-dark ${scrolled || mobileOpen ? "is-solid" : ""}` : "sticky top-0 z-40 w-full header-dark is-solid"}>

      <div className="header-bar">
        <div className="mx-auto flex max-w-none items-center gap-4 px-4 py-3 md:gap-8 md:px-6 md:py-4">
          <button
            type="button"
            aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMobileOpen((prev) => !prev)}
            className="mobile-menu-toggle flex h-11 w-11 items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 lg:hidden"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>

          <Logo />

          <nav className="desktop-navigation hidden flex-1 items-center justify-center gap-1 lg:flex">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.children && setActiveDropdown(item.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={item.href}
                  aria-current={item.href === location.pathname + location.search ? "page" : undefined}
                  className="flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:text-primary-700"
                >
                  {item.label}
                  {item.children && <i className="ri-arrow-down-s-line text-base"></i>}
                </Link>
                {item.children && activeDropdown === item.label && (
                  <div className="absolute left-0 top-full z-50 max-h-[70vh] w-56 overflow-y-auto rounded-lg border border-background-200 bg-background-50 py-2 shadow-lg">
                    <Link
                      to="/produtos"
                      onClick={() => setActiveDropdown(null)}
                      className="block px-4 py-2 text-sm font-medium text-foreground-900 transition-colors hover:bg-background-100 hover:text-primary-700"
                    >
                      Todos os produtos
                    </Link>
                    {storeCategories.map((category) => (
                      <Link
                        key={category.id}
                        to={categoryHref(category.id)}
                        onClick={() => setActiveDropdown(null)}
                        className="block px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                      >
                        {category.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-1 md:ml-0 md:gap-2">
            <Link to="/meus-pedidos" className="header-track" aria-label="Rastrear pedido">
              <PackageSearch size={20} aria-hidden="true" />
              <span>Rastrear Pedido</span>
            </Link>

            <button
              type="button"
              aria-label="Abrir carrinho"
              onClick={openCart}
              className="relative flex h-11 w-11 items-center justify-center rounded-full text-foreground-800 transition-colors hover:bg-background-100 hover:text-primary-700"
            >
              <i className="ri-shopping-cart-2-line text-xl"></i>
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-semibold text-background-50">
                {totalItems}
              </span>
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div id="mobile-navigation" className="mobile-navigation max-h-[70dvh] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
            <nav className="flex flex-col">
              {navItems.map((item) =>
                item.children ? (
                  <div key={item.label} className="border-b border-background-100">
                    <button
                      type="button"
                      aria-expanded={mobileCategoriesOpen}
                      onClick={() => setMobileCategoriesOpen((open) => !open)}
                      className="flex min-h-[44px] w-full items-center justify-between py-2.5 text-sm font-medium text-foreground-800"
                    >
                      {item.label}
                      <i className={`${mobileCategoriesOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-base`}></i>
                    </button>
                    {mobileCategoriesOpen && (
                      <div className="flex flex-col pb-2 pl-3">
                        <Link to="/produtos" onClick={() => setMobileOpen(false)} className="flex min-h-[44px] items-center text-sm font-medium text-foreground-900">
                          Todos os produtos
                        </Link>
                        {storeCategories.map((category) => (
                          <Link
                            key={category.id}
                            to={categoryHref(category.id)}
                            onClick={() => setMobileOpen(false)}
                            className="flex min-h-[44px] items-center text-sm text-foreground-700"
                          >
                            {category.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex min-h-[44px] items-center border-b border-background-100 py-2.5 text-sm font-medium text-foreground-800"
                  >
                    {item.label}
                  </Link>
                )
              )}
              <Link
                to="/meus-pedidos"
                onClick={() => setMobileOpen(false)}
                className="track-order-cta mt-3 flex items-center justify-center gap-2 rounded-full bg-foreground-950 py-3 text-sm font-semibold text-background-50"
              >
                <PackageSearch size={16} className="text-primary-400" />
                Rastrear Pedido
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}



